import React, { useState, useRef, useEffect } from "react";
import {
  Send,
  Phone,
  Video,
  MoreVertical,
  ArrowLeft,
} from "lucide-react";
import "./ChatInterface.css";

const ChatInterface = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hey! How's your day going?",
      sender: "other",
      timestamp: "10:30 AM",
    },
    {
      id: 2,
      text: "Pretty good! Just finished a big project. How about you?",
      sender: "me",
      timestamp: "10:32 AM",
    },
    {
      id: 3,
      text: "That's awesome! I'm just relaxing today. Want to grab coffee later?",
      sender: "other",
      timestamp: "10:35 AM",
    },
    {
      id: 4,
      text: "Sounds perfect! Let's meet at the usual place around 3?",
      sender: "me",
      timestamp: "10:36 AM",
    },
    {
      id: 5,
      text: "Perfect! See you then 😊",
      sender: "other",
      timestamp: "10:37 AM",
    },
  ]);

  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = 24; // Approximate line height
      const maxHeight = lineHeight * 8; // 8 lines max
      const minHeight = lineHeight * 1; // 1 line min

      textarea.style.height =
        Math.min(Math.max(scrollHeight, minHeight), maxHeight) + "px";
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  const handleSend = () => {
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        text: newMessage,
        sender: "me",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages([...messages, message]);
      setNewMessage("");

      // Simulate typing indicator and response
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const response = {
          id: messages.length + 2,
          text: "That sounds great! Looking forward to it 🎉",
          sender: "other",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          avatar: "👩‍💼",
        };
        setMessages((prev) => [...prev, response]);
      }, 2000);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 max-w-md mx-auto relative overflow-hidden">
      {/* Header */}
      {/* <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center space-x-3">
          <ArrowLeft className="w-6 h-6 cursor-pointer hover:bg-white/20 rounded-full p-1 transition-colors" />
          <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-lg font-bold shadow-md">
            👩‍💼
          </div>
          <div>
            <p className="font-semibold text-lg">Sarah Chen</p>
            <p className="text-blue-100 text-sm">Online</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Phone className="w-6 h-6 cursor-pointer hover:bg-white/20 rounded-full p-1 transition-colors" />
          <Video className="w-6 h-6 cursor-pointer hover:bg-white/20 rounded-full p-1 transition-colors" />
          <MoreVertical className="w-6 h-6 cursor-pointer hover:bg-white/20 rounded-full p-1 transition-colors" />
        </div>
      </div> */}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "me" ? "justify-end" : "justify-start"
            } animate-fade-in`}
          >
            <div
              className={`flex max-w-xs lg:max-w-md ${
                message.sender === "me" ? "flex-row-reverse" : "flex-row"
              } items-end space-x-2`}
            >
              <div className="flex flex-col">
                <div
                  className={`px-2 py-2 rounded-2xl shadow-md transition-all duration-200 hover:shadow-lg ${
                    message.sender === "me"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-br-md"
                      : "bg-white text-gray-800 rounded-bl-md border border-gray-100"
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                </div>
                <p
                  className={`text-xs text-gray-500 mt-1 ${
                    message.sender === "me" ? "text-right" : "text-left"
                  }`}
                >
                  {message.timestamp}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex justify-start animate-fade-in">
            <div className="flex items-end space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                👩‍💼
              </div>
              <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-md border border-gray-100">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      {/* Input Area */}
      <div className="bg-white/80 backdrop-blur-md border-t border-white/20 px-4 py-4 shadow-2xl">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleInputChange}
            // onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="w-full pl-2 pr-12 py-2 border-2 border-gray-200/60 rounded-2xl focus:border-indigo-400 focus:outline-none resize-none text-sm bg-white/90 backdrop-blur-sm transition-all duration-200 font-medium placeholder-gray-400 shadow-inner hover:shadow-md focus:shadow-lg"
            style={{
              minHeight: "54px",
              lineHeight: "27px",
              maxHeight: "216px", // 8 lines * 27px
            }}
            rows="1"
          />

          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className={`absolute right-3 bottom-4 p-2 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg ${
              newMessage.trim()
                ? "bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-600 text-white hover:scale-105 active:scale-95 shadow-indigo-200"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
